'use strict';

/**
 * Per-intent Soren cash cards (CR-11). Pure markup + classify for goldens.
 * Statement / what-if / forecast — not a blanket "hide prose when shaped".
 */

import { isCashCardQuestion } from './sorenOpenCashCard.js';
import { overdueCardHtml, followupCardHtml } from './sorenOverdueCardHtml.js';

function detectStatementIntent(text) {
  const raw = String(text || '').trim();
  const t = raw.toLowerCase();
  if (/\b(cash|progress|ops|operations)\s+report\b/.test(t)) return false;
  if (!/\b(report|statement|pending invoices)\b/.test(t)) return false;
  return /\b(?:for|from)\s+[A-Za-z]/.test(raw);
}

function detectWhatIfIntent(text) {
  const raw = String(text || '').trim();
  if (!/\blate\b/i.test(raw)) return false;
  return /what if/i.test(raw)
    || /\bif\b.+\bpay/i.test(raw)
    || /\bpays?\b.{0,24}\b(?:a\s+week|\d+\s+days?)\s+late\b/i.test(raw);
}

function directionFromDelta(delta) {
  const amt = delta && delta.amount != null ? Number(delta.amount) : NaN;
  if (!Number.isFinite(amt) || amt === 0) return 'unchanged';
  return amt < 0 ? 'lower' : 'higher';
}

function detectOverdueIntent(text) {
  const t = String(text || '').trim();
  if (!t) return false;
  if (detectFollowUpIntent(t)) return false;
  return /\b(overdue|past due)\b/i.test(t)
    || /\bwhat'?s\s+late\b/i.test(t)
    || /\bhow much\b.{0,32}\b(late|overdue)\b/i.test(t)
    || /\bmoney is overdue\b/i.test(t);
}

function detectFollowUpIntent(text) {
  const t = String(text || '').trim();
  if (!t) return false;
  return /\bcollect my money\b/i.test(t)
    || /\bdraft follow-?ups?\b/i.test(t)
    || /\bdo a follow-?up\b/i.test(t)
    || /\bsend (a |the )?follow-?ups?\b/i.test(t)
    || /\bfollow[- ]ups?\b/i.test(t);
}

function detectForecastIntent(text) {
  const raw = String(text || '').trim();
  if (detectStatementIntent(raw) || detectWhatIfIntent(raw)) return false;
  if (detectOverdueIntent(raw) || detectFollowUpIntent(raw)) return false;
  if (isCashCardQuestion(raw)) return true;
  if (/\bcash report pdf\b/i.test(raw) || /\bsend me the cash report\b/i.test(raw)) return true;
  return /\b(short|dip|runway|collect now|am i short|this week or next|what changed this week)\b/i.test(raw)
    || /\bcash (this|next) week\b/i.test(raw)
    || /when will.+\spay/i.test(raw);
}

function classifySorenAnswer(question, digest) {
  const kind = digest && digest.kind;
  if (kind === 'overdue_collections') return 'overdue';
  if (kind === 'overdue_followup_drafts') return 'followup';
  if (kind === 'owner_statement') return 'statement';
  if (kind === 'what_if') return 'what_if';
  if (kind === 'cash_report_pdf') return 'cash_report_pdf';
  if (kind === 'cash_forecast') return 'forecast';
  if (kind === 'pulse') return 'pulse';
  if (detectFollowUpIntent(question)) return 'followup';
  if (detectOverdueIntent(question)) return 'overdue';
  if (detectStatementIntent(question)) return 'statement';
  if (detectWhatIfIntent(question)) return 'what_if';
  if (detectForecastIntent(question)) return 'forecast';
  return null;
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Only /s/:token/pdf on our hosts. Never protocol-relative or foreign origins. */
function safeStatementPdfHref(url) {
  const raw = String(url || '').trim();
  if (!raw) return null;
  const pathOk = (p) => /^\/s\/[A-Za-z0-9_-]+\/pdf$/.test(String(p || '').split('?')[0].split('#')[0]);
  if (raw.startsWith('/') && !raw.startsWith('//')) {
    const path = raw.split('?')[0].split('#')[0];
    return pathOk(path) ? path : null;
  }
  try {
    const u = new URL(raw);
    if (!/^https?:$/.test(u.protocol)) return null;
    const host = String(u.hostname || '').toLowerCase();
    const ours = host === 'localhost'
      || host === 'constructinv.varshyl.com'
      || host.endsWith('.constructinv.varshyl.com')
      || host === 'soviqai.com'
      || host === 'app.soviqai.com'
      || host.endsWith('.soviqai.com');
    if (!ours) return null;
    return pathOk(u.pathname) ? u.pathname : null;
  } catch (_) { /* ignore */ }
  return null;
}

function safeCashReportPdfHref(url) {
  const raw = String(url || '').trim();
  if (!raw) return null;
  const pathOk = (p) => /^\/s\/[A-Za-z0-9_-]+\/cash-report\/pdf$/.test(String(p || '').split('?')[0].split('#')[0]);
  if (raw.startsWith('/') && !raw.startsWith('//')) {
    const path = raw.split('?')[0].split('#')[0];
    return pathOk(path) ? path : null;
  }
  try {
    const u = new URL(raw);
    if (!/^https?:$/.test(u.protocol)) return null;
    const host = String(u.hostname || '').toLowerCase();
    const ours = host === 'localhost'
      || host === 'constructinv.varshyl.com'
      || host.endsWith('.constructinv.varshyl.com')
      || host === 'soviqai.com'
      || host === 'app.soviqai.com'
      || host.endsWith('.soviqai.com');
    if (!ours) return null;
    return pathOk(u.pathname) ? u.pathname : null;
  } catch (_) { /* ignore */ }
  return null;
}

function statementCardHtml(digest) {
  const href = safeStatementPdfHref(digest && digest.download_url);
  const owner = escapeHtml((digest && digest.owner_name) || 'Owner');
  const projects = Array.isArray(digest && digest.projects) ? digest.projects : [];
  const rows = projects.map((p) => {
    const name = escapeHtml(p.project_name || 'Project');
    const amt = escapeHtml((p.outstanding && p.outstanding.formatted) || '$0.00');
    return `<li data-testid="soren-statement-project"><span>${name}</span><strong>${amt}</strong></li>`;
  }).join('');
  const total = escapeHtml(
    (digest && digest.outstanding && digest.outstanding.formatted)
    || (digest && digest.summary && digest.summary.outstanding && digest.summary.outstanding.formatted)
    || '$0.00',
  );
  const download = href
    ? `<a class="cr-btn dark" data-testid="soren-statement-download" href="${escapeHtml(href)}">Download PDF</a>`
    : '';
  return `<div class="soren-statement-answer" data-testid="soren-statement-answer">
  <h3>Owner statement · ${owner}</h3>
  <ul class="soren-fact-list">${rows}</ul>
  <p data-testid="soren-statement-total"><span>Total</span> <strong class="money">${total}</strong></p>
  ${download}
  <button type="button" class="cr-btn" data-testid="soren-open-statement">Open statement</button>
</div>`;
}

function whatIfCardHtml(digest) {
  const base = escapeHtml(
    (digest && digest.base && digest.base.lowest_point && digest.base.lowest_point.formatted) || '—',
  );
  const next = escapeHtml(
    (digest && digest.what_if && digest.what_if.lowest_point && digest.what_if.lowest_point.formatted) || '—',
  );
  const week = escapeHtml((digest && digest.what_if && digest.what_if.lowest_week) || '—');
  const delta = escapeHtml((digest && digest.delta && digest.delta.formatted) || '—');
  const dir = directionFromDelta(digest && digest.delta);
  const dirBit = dir !== 'unchanged' ? ` ${escapeHtml(dir)}` : '';
  return `<div class="soren-whatif-answer" data-testid="soren-whatif-answer">
  <p data-testid="soren-whatif-lowest">${base} → ${next}</p>
  <p data-testid="soren-whatif-week">Week of ${week}</p>
  <p data-testid="soren-whatif-delta">${delta}${dirBit}</p>
</div>`;
}

function forecastCardHtml() {
  return `<div class="soren-engine-answer" data-testid="soren-cash-answer"></div>`;
}

export {
  classifySorenAnswer,
  detectStatementIntent,
  detectWhatIfIntent,
  detectForecastIntent,
  detectOverdueIntent,
  detectFollowUpIntent,
  directionFromDelta,
  safeStatementPdfHref,
  safeCashReportPdfHref,
  statementCardHtml,
  whatIfCardHtml,
  forecastCardHtml,
  overdueCardHtml,
  followupCardHtml,
};
